import type { FontIntegrityManifest } from "@/composables/useFontIntegrity"
import { getFontStylesheetIntegrity } from "@/utils/fontIntegrity"

interface FontStylesheetLink {
  rel: string
  href: string
  integrity?: string
  crossorigin?: string
}

/**
 * Adds the integrity hash to a font stylesheet link, when the manifest lists that font.
 *
 * `crossorigin` comes along necessarily: SRI is only verified on a cross-origin request
 * made in CORS mode, so a stylesheet carrying `integrity` without it would be blocked
 * outright instead of checked. The font CDN answers with `access-control-allow-origin: *`.
 */
function withIntegrity(link: FontStylesheetLink, manifest: FontIntegrityManifest): FontStylesheetLink {
  const integrity = getFontStylesheetIntegrity(link.href, manifest)

  if (!integrity)
    return link

  return { ...link, integrity, crossorigin: "anonymous" }
}

/**
 * @param jobShop The JobShop whose configured fonts should be loaded.
 * @param fontIntegrity SRI hashes per font folder, already gated by the
 * `isFontSriEnabled` flag — empty when the feature is off for this tenant or the manifest
 * is unavailable. Passed in rather than resolved here: this composable runs after an
 * await in `layouts/default.vue`, where `useFeatureFlag` and the fetch behind it would
 * break (see the async setup constraint in AGENTS.md).
 */
export function useFonts(jobShop: FontSettingsSource | null, fontIntegrity: FontIntegrityManifest = {}) {
  const { public: { tcFontCdnURL } } = useRuntimeConfig()

  // sets selected fonts & font-weights
  const selectedFontHeading = jobShop?.["fonts-and-colors"].config.general.fonts.heading
  const selectedFontBody = jobShop?.["fonts-and-colors"].config.general.fonts.body
  const selectedFontButton = jobShop?.["fonts-and-colors"].config.general.fonts.button
  const selectedFontWeightHeading = jobShop?.["fonts-and-colors"].config.general.fontWeights.heading
  const selectedFontWeightBody = jobShop?.["fonts-and-colors"].config.general.fontWeights.body
  const selectedFontWeightButton = jobShop?.["fonts-and-colors"].config.general.fontWeights.button

  if (import.meta.client) {
    document.documentElement.style.setProperty("--cr-heading-font", `"${selectedFontHeading?.value}"`)
    document.documentElement.style.setProperty("--cr-body-font", `"${selectedFontBody?.value}"`)
    document.documentElement.style.setProperty("--cr-button-font", `"${selectedFontButton?.value}"`)
    document.documentElement.style.setProperty("--cr-heading-font-weight", `${selectedFontWeightHeading?.value}`)
    document.documentElement.style.setProperty("--cr-body-font-weight", `${selectedFontWeightBody?.value}`)
    document.documentElement.style.setProperty("--cr-button-font-weight", `${selectedFontWeightButton?.value}`)
  }

  // Load font-face definition CSS from CDN for selected custom font(s)
  const fontLinks: FontStylesheetLink[] = [{ rel: "stylesheet", href: `${tcFontCdnURL}/fonts/${selectedFontHeading?.value}/style.css` }]
  if (selectedFontHeading?.value !== selectedFontBody?.value)
    fontLinks.push({ rel: "stylesheet", href: `${tcFontCdnURL}/fonts/${selectedFontBody?.value}/style.css` })

  if (selectedFontBody?.value !== selectedFontButton?.value)
    fontLinks.push({ rel: "stylesheet", href: `${tcFontCdnURL}/fonts/${selectedFontButton?.value}/style.css` })

  useHead({
    link: fontLinks.map(link => withIntegrity(link, fontIntegrity)),
  })
}
